import axios from 'axios'
import { setCookie } from 'cookie'
import {
  createCreditCardUrl,
  deleteCreditCardUrl,
  changePlanUrl,
  getStripeInvoicesUrl,
  createCustomerChargeUrl,
  getCustomerInfoUrl,
  updateAutoDepositSettingUrl,
  getCustomerTransactionBalanceUrl,
  updateAddOnUrl,
  getUserCreditsUrl,
  getAiCreditPacksUrl,
  getAiCreditHistoryUrl,
  purchaseAiCreditsUrl,
  getAiAutoReloadSettingUrl,
  updateAiAutoReloadSettingUrl,
} from 'urls'
import { topRedirect } from 'utils'
import { sendGtmPixel, pickPrimaryStoreAccount } from 'tagManager'
import { fireRefersionTracking } from 'tracking'
import { checkRequiredAction } from './actionUtils'
import _ from 'lodash'
import { getAxiosDefaultConfig } from 'utils'

// Extract a user-facing message from a billing API error, tolerating both the
// Stripe shape ({ error: { raw: { message } } }) and the Shopify/plain shape
// ({ error: '<message>' } or { message }). Without this, non-Stripe errors like
// the Shopify "please re-authorize" 400 fall through to the generic "Internal Error".
const extractBillingError = (error) => {
  const data = _.get(error, ['response', 'data'], {}) || {}
  return _.get(data, ['error', 'raw', 'message'])
    || (typeof data.error === 'string' ? data.error : null)
    || (typeof data.message === 'string' ? data.message : null)
    || 'Internal Error'
}

// Plan hierarchy for determining upgrade/downgrade
const planHierarchy = {
  'Free': 0,
  'Entrepreneur': 1,
  'Professional': 2, // retired; kept so grandfathered subscribers classify up/downgrades correctly
  'Legacy Business': 3,
  'Business': 4,
  'Enterprise': 5,
}

// Get affiliate plan change type
const getAfftrackPlanChangeType = (oldPlan, newPlan, toCancel, isNewPaidCustomer) => {
  const oldProduct = oldPlan?.product || 'Free'
  const newProduct = newPlan

  if (toCancel || newProduct === 'Free') {
    return 'afftrack_plan_churn'
  }
  if (isNewPaidCustomer) {
    return 'afftrack_trial_conversion'
  }
  const oldLevel = planHierarchy[oldProduct] ?? 0
  const newLevel = planHierarchy[newProduct] ?? 0
  if (newLevel > oldLevel) {
    return 'afftrack_plan_upgrade'
  }
  if (newLevel < oldLevel) {
    return 'afftrack_plan_downgrade'
  }
  // Same level but different interval - treat as upgrade for billing changes
  return 'afftrack_plan_upgrade'
}

// Get plan price based on interval
const getPlanPrice = (planOption, interval, intervalCount) => {
  if (interval === 'month') {
    return planOption?.priceMonth || planOption?.amount / 100 || 0
  }
  if (intervalCount === 1) {
    return planOption?.priceYear || planOption?.amount / 100 || 0
  }
  if (intervalCount === 2) {
    return planOption?.priceTwoYear || planOption?.amount / 100 || 0
  }
  return planOption?.priceThreeYear || planOption?.amount / 100 || 0
}

export function setView(viewIndex) {
  return {
    type: 'SET_VIEW_PLAN',
    payload: viewIndex,
  }
}

export function setStripeLoaded(flag) {
  return {
    type: 'SET_STRIPE_LOADED_PLAN',
    payload: flag,
  }
}

export function toggleManagementModal(option) {
  return {
    type: 'TOGGLE_PAYMENT_MODAL_PLAN',
    payload: option,
  }
}

export function setUpgradeDismissed(flag) {
  return {
    type: 'SET_UPGRADE_DISMISSED_PLAN',
    payload: flag,
  }
}

export function setAddOnUpgradeDismissed(addOnDismissed) {
  return {
    type: 'SET_ADD_ON_UPGRADE_DISMISSED_PLAN',
    payload: addOnDismissed,
  }
}

export function setLoadingView(view) {
  return {
    type: 'SET_LOADING_VIEW_PLAN',
    payload: view,
  }
}

export function selectCreditCard(card) {
  return {
    type: 'SELECT_CREDIT_CARD_PLAN',
    payload: card,
  }
}

export function selectBillingAddress(address) {
  return {
    type: 'SELECT_BILLING_ADDRESS_PLAN',
    payload: address,
  }
}

export function selectPlanOption(option) {
  return {
    type: 'SELECT_PLAN_OPTION_PLAN',
    payload: option,
  }
}

export function selectAddOn(addOn) {
  return {
    type: 'SELECT_ADD_ON_PLAN',
    payload: addOn,
  }
}

export function setUpsellAddOn(addOn) {
  return {
    type: 'SET_UPSELL_ADD_ON_PLAN',
    payload: addOn,
  }
}

export function setNextModalBody(name) {
  return {
    type: 'SET_NEXT_MODAL_BODY_PLAN',
    payload: name,
  }
}

export function getUserCredits() {
  return async (dispatch) => {
    const { data: resData } = await axios.get(getUserCreditsUrl, getAxiosDefaultConfig())
    dispatch({ type: 'SET_USER_CREDITS_PLAN', payload: resData.userCredits })
    if (resData.aiCreditAllowance !== undefined) {
      dispatch({ type: 'SET_AI_CREDIT_ALLOWANCE_PLAN', payload: resData.aiCreditAllowance })
    }
  }
}

function applyAiCreditBalanceFromSetting(dispatch, getState, setting) {
  if (setting && typeof setting.creditBalance === 'number') {
    const current = getState().plan.userCredits || {}
    dispatch({
      type: 'SET_USER_CREDITS_PLAN',
      payload: { ...current, ai: setting.creditBalance },
    })
  }
}

export function setUserCredits(userCredits) {
  return {
    type: 'SET_USER_CREDITS_PLAN',
    payload: userCredits,
  }
}

export function setCreditCards(creditCards) {
  return {
    type: 'SET_CREDIT_CARDS_PLAN',
    payload: creditCards,
  }
}

export function setSubscription(subscription) {
  return {
    type: 'SET_SUBSCRIPTION_PLAN',
    payload: subscription,
  }
}

export function setBillingChannel(billingChannel) {
  return {
    type: 'SET_BILLING_CHANNEL_PLAN',
    payload: billingChannel,
  }
}

export function setRedirectedAction(action) {
  return {
    type: 'SET_REDIRECT_ACTION_PLAN',
    payload: action,
  }
}

export function setBillingMessage(message) {
  return {
    type: 'SET_BILLING_MESSAGE_PLAN',
    payload: message,
  }
}

export function setCustomerBalanceLoad(action) {
  return {
    type: 'SET_CUSTOMER_BALANCE_LOAD',
    payload: action
  }
}

export function setBillingError(error) {
  return {
    type: 'SET_BILLING_ERROR_PLAN',
    payload: typeof error === 'string' ? error : 'There was an error during processing. Please try again.',
  }
}

export function applyNewCreditCard(currentCards, newCreditCard, subscription) {
  return (dispatch) => {
    dispatch({ type: 'SET_CREDIT_CARDS_PLAN', payload: [...currentCards, newCreditCard] })
    dispatch({ type: 'SELECT_CREDIT_CARD_PLAN', payload: newCreditCard})
    dispatch({ type: 'SET_SUBSCRIPTION_PLAN', payload: {
      ...subscription,
      plan_status: {
        ...subscription.plan_status,
        creditCardRequired: false,
      }
    }})
  }
}

export function updateCreditCard(currentCards, token, action, subscription, callback, onError?) {
  return async (dispatch, getState) => {
    try {
      const nextCardsDeleted = currentCards.filter(card => card.id !== token)
      const nextDefaultCard = nextCardsDeleted.length ? nextCardsDeleted[0] : null
      const nextDefaultToken = nextDefaultCard ? nextDefaultCard.id : null
      const addingCard = action === 'add'
      const { data: resData } = await axios.post(
        addingCard ? createCreditCardUrl : deleteCreditCardUrl,
        { token, nextDefaultToken },
        getAxiosDefaultConfig()
      )
      const nextCards = addingCard ? [resData, ...currentCards] : nextCardsDeleted
      const nextSelectedCard = addingCard ? resData : nextDefaultCard
      dispatch({ type: 'SET_LOADING_VIEW_PLAN', payload: '' })
      dispatch({ type: 'SET_CREDIT_CARDS_PLAN', payload: nextCards })
      dispatch({ type: 'SELECT_CREDIT_CARD_PLAN', payload: nextSelectedCard})
      dispatch({ type: 'SET_BILLING_ERROR_PLAN', payload: null })
      dispatch({ type: 'SET_SUBSCRIPTION_PLAN', payload: {
        ...subscription,
        plan_status: {
          ...subscription.plan_status,
          creditCardRequired: nextCards.length === 0,
        }
      }})
      if (addingCard) {
        sendGtmPixel({
          ...resData,
          storeAccount: pickPrimaryStoreAccount(getState().root.accounts),
        }, 'cc_added')
        dispatch({ type: 'SET_PAYMENT_TRANSFER_PENDING_PLAN', payload: null })
      }
      if (callback) callback()
      if (resData.new_paid_customer) {
        dispatch({ type: 'SET_PLAN_NEW_PAID_CUSTOMER', payload: resData.new_paid_customer })
      }
    }
    catch(error) {
      console.error(error)
      const errorMessage = _.get(error, ['response', 'data', 'error'], 'Internal Error')
      dispatch({ type: 'SET_LOADING_VIEW_PLAN', payload: '' })
      dispatch({ type: 'SET_BILLING_ERROR_PLAN', payload: errorMessage })
      if (onError) onError(errorMessage)
    }
  }
}

const successMsg = 'Your plan has been updated successfully. Thank you.'
const failureMsg = 'The charge was not successful. Please try again.'
const timeoutMsg = 'The session was timed out. Please try again.'

export function updatePlan(
  subscription, product, interval, intervalCount, creditCards, selectedCreditCard = {}, callback, topUrl, newPaidCustomerFromCcEnter,
  toCancel, { cancelReason, cancelReasonSpecified }, seats = 1
) {
  const oldPlan = subscription.plans[0]
  return async (dispatch, getState) => {
    try {
      const { user: { email } } = getState().root
      const businessPrice = _.get(getState().plan, ['subscription', 'plan_status', 'businessPrice'])
      const { data: { addOns: nextAddOns, ...resData} } = await axios.post(
        changePlanUrl,
        {
          product, interval, intervalCount, creditCards, subscriptionId: subscription.id, token: (selectedCreditCard || {}).id,
          toCancel, cancelReason, cancelReasonSpecified, businessPrice, seats,
        },
        getAxiosDefaultConfig()
      )
      const planAction = {type: 'new_plan', oldPlan, product, interval, intervalCount, date: (new Date()).toString()}
      if (resData.redirect_url) {
        // Shopify App Pricing: send the merchant to Shopify's hosted plan page.
        // When embedded (App Bridge present), navigate the TOP admin frame in the
        // SAME tab via open(url, '_top') — App Bridge relays this to the host, so
        // there's no new tab and no cross-origin iframe issue. Shopify returns to
        // the app afterwards and reconcile-on-load reflects the new plan.
        // Non-embedded (no App Bridge): fall back to a new tab (topRedirect polls
        // for close and refreshes so the plan still updates).
        // Land back on the Plan page (not the dashboard) when Shopify returns.
        try { localStorage.setItem('payhelm_return_to_plan', '1') } catch { /* no-op */ }
        const hasAppBridge = typeof window !== 'undefined' && !!window.shopify
        if (hasAppBridge) {
          window.open(resData.redirect_url, '_top')
        } else {
          topRedirect({ url: resData.redirect_url, newWindow: true, successMessage: 'Choose your plan in the new Shopify tab' })
        }
        dispatch({ type: 'SET_LOADING_VIEW_PLAN', payload: '' })
        if (callback) callback()
        return
      }
      if (resData.confirmation_url) {
        const waitingTime = 1000*60*20

        setCookie('confirming_charge', 'true', waitingTime)
        topRedirect({ url: resData.confirmation_url, waitingTime, newWindow: true, successMessage: 'Your plan option is updated' })
      }
      else {
        const { subscriptionActive, requiredAction } = checkRequiredAction({ subscription: resData }, getState().root.admin)
        dispatch({ type: 'SET_DONE_ACTION_INTERCOM', payload: planAction })
        dispatch({ type: 'SET_DONE_ACTION_INTERCOM', payload: requiredAction })
        dispatch({ type: 'SET_SUBSCRIPTION_PLAN', payload: resData })
        if (nextAddOns) {
          dispatch({ type: 'SET_ADD_ONS_PLAN', payload: nextAddOns })
        }
        dispatch({ type: 'SET_BILLING_MESSAGE_PLAN', payload: { text: successMsg, type: 'success' } })
        dispatch({ type: 'SET_BILLING_ERROR_PLAN', payload: null })
        dispatch({ type: 'SET_SUBSCRIPTION_ACTIVE_PLAN', payload: subscriptionActive })
        dispatch({ type: 'SET_PLAN_EXPIRED_PLAN', payload: false })
        dispatch(getUserCredits()).catch(() => {}) // non-blocking refresh
        if (callback) {
          callback()
        }

        const newPaidCustomerFromPlanOrder = resData.new_paid_customer
        if ((newPaidCustomerFromCcEnter || newPaidCustomerFromPlanOrder) && resData.plans && resData.plans.length) {
          const planOption = resData.plans[0]
          fireRefersionTracking(planOption, newPaidCustomerFromCcEnter || newPaidCustomerFromPlanOrder, subscription?.id || resData.id)
        }
      }
      const storeAccount = pickPrimaryStoreAccount(getState().root.accounts)
      if (toCancel) {
        sendGtmPixel({ email, oldPlan, cancelReason, cancelReasonSpecified, storeAccount }, 'plan_cancelled')
      }
      else {
        sendGtmPixel({ email, oldPlan, newPlan: product, storeAccount }, 'plan_changed')
      }

      // Affiliate tracking for plan changes - only fire for known plan products
      const planProducts = ['Free', 'Entrepreneur', 'Professional', 'Legacy Business', 'Business', 'Enterprise']
      if (planProducts.includes(product)) {
        const userId = getState().root.user?.id
        const isNewPaidCustomer = newPaidCustomerFromCcEnter || resData.new_paid_customer
        const afftrackEventType = getAfftrackPlanChangeType(oldPlan, product, toCancel, isNewPaidCustomer)
        const frequency = interval === 'month' ? 'monthly' : 'yearly'
        const amount = resData.plans?.[0]?.amount ? resData.plans[0].amount / 100 : 0

        sendGtmPixel({
          email,
          userId,
          plan: product,
          frequency,
          amount,
          storeAccount,
        }, afftrackEventType)
      }
    }
    catch(error) {
      console.log(error)
      const errorMessage = extractBillingError(error)
      dispatch({ type: 'SET_LOADING_VIEW_PLAN', payload: '' })
      dispatch({ type: 'SET_BILLING_ERROR_PLAN', payload: errorMessage })
    }
  }
}

export function updateAddOn(
  mainSubscriptionId,
  subscription,
  product,
  interval,
  intervalCount,
  creditCards,
  selectedCreditCard = {},
  callback,
  topUrl,
  newPaidCustomerFromCcEnter,
  toCancel,
  { cancelReason, cancelReasonSpecified }
) {
  const oldPlan = subscription?.plans[0] || null
  const subscriptionId = subscription?.id || null
  return async (dispatch, getState) => {
    try {
      const { user: { email } } = getState().root
      const { addOns } = getState().plan
      // const businessPrice = _.get(getState().plan, ['subscription', 'plan_status', 'businessPrice'])
      const { data: { subscription, ...resData } } = await axios.post(
        updateAddOnUrl,
        {
          product, interval, intervalCount, creditCards, subscriptionId, token: (selectedCreditCard || {}).id,
          toCancel, cancelReason, cancelReasonSpecified, mainSubscriptionId
        },
        getAxiosDefaultConfig()
      )
      const planAction = {type: 'new_plan', oldPlan, product, interval, intervalCount, date: (new Date()).toString()}
      if (resData.confirmation_url) {
        const waitingTime = 1000*60*20

        setCookie('confirming_charge', 'true', waitingTime)
        topRedirect({ url: resData.confirmation_url, waitingTime, newWindow: true, successMessage: 'Your plan option is updated' })
      }
      else {
        const { subscriptionActive, requiredAction } = checkRequiredAction({ subscription: resData }, getState().root.admin)
        const nextAddOns = toCancel ? addOns.filter(a => a.plans[0].product !== product) : addOns.reduce((acc, a) => {
          return [
            ...acc,
            ...(a.plans[0].product === product ? [] : [a]),
          ]
        }, [resData])
        console.log(nextAddOns)
        dispatch({ type: 'SET_DONE_ACTION_INTERCOM', payload: planAction })
        // dispatch({ type: 'SET_DONE_ACTION_INTERCOM', payload: requiredAction })
        dispatch({ type: 'SET_ADD_ONS_PLAN', payload: nextAddOns })
        dispatch({ type: 'SET_BILLING_MESSAGE_PLAN', payload: { text: successMsg, type: 'success' } })
        dispatch({ type: 'SET_BILLING_ERROR_PLAN', payload: null })
        if (subscription) {
          dispatch({ type: 'SET_SUBSCRIPTION_PLAN', payload: subscription })
        }
        // dispatch({ type: 'SET_SUBSCRIPTION_ACTIVE_PLAN', payload: subscriptionActive })
        // dispatch({ type: 'SET_PLAN_EXPIRED_PLAN', payload: false })
        dispatch(getUserCredits()).catch(() => {}) // non-blocking refresh
        if (callback) {
          callback()
        }

        const newPaidCustomerFromPlanOrder = resData.new_paid_customer
        if ((newPaidCustomerFromCcEnter || newPaidCustomerFromPlanOrder) && resData.plans && resData.plans.length) {
          const planOption = resData.plans[0]
          fireRefersionTracking(planOption, newPaidCustomerFromCcEnter || newPaidCustomerFromPlanOrder, subscription?.id || resData.id)
        }
      }
      const storeAccount = pickPrimaryStoreAccount(getState().root.accounts)
      if (toCancel) {
        sendGtmPixel({ email, oldPlan, cancelReason, cancelReasonSpecified, storeAccount }, 'plan_cancelled')
      }
      else {
        sendGtmPixel({ email, oldPlan, newPlan: product, storeAccount }, 'plan_changed')
      }

      // Affiliate tracking for addon changes
      const userId = getState().root.user?.id
      const afftrackEventType = toCancel ? 'afftrack_addon_remove' : 'afftrack_addon_add'
      const frequency = interval === 'month' ? 'monthly' : 'yearly'
      const amount = resData.plans?.[0]?.amount ? resData.plans[0].amount / 100 : 0

      sendGtmPixel({
        email,
        userId,
        addon: product,
        frequency,
        amount,
        storeAccount,
      }, afftrackEventType)
    }
    catch(error) {
      console.log(error)
      const errorMessage = extractBillingError(error)
      dispatch({ type: 'SET_LOADING_VIEW_PLAN', payload: '' })
      dispatch({ type: 'SET_BILLING_ERROR_PLAN', payload: errorMessage })
    }
  }
}

export function setSubscriptionActive(flag) {
  return {
    type: 'SET_SUBSCRIPTION_ACTIVE_PLAN',
    payload: flag,
  }
}

export function setPlanExpired(flag) {
  return {
    type: 'SET_PLAN_EXPIRED_PLAN',
    payload: flag,
  }
}

export function getStripeInvoices() {
  return async (dispatch, getState) => {
    try {
      const { data: resData } = await axios.get(
        getStripeInvoicesUrl,
        getAxiosDefaultConfig()
      )

      const invoices = resData.filter(invoice => invoice.amount_due || invoice.statement_descriptor === 'PayHelm' || invoice.statement_descriptor === 'PayHelm Business').map(invoice => {

        if (invoice.object === 'invoice') {
          return {
            ...invoice,
            amount_due: invoice.amount_due / 100,
            amount_paid: invoice.amount_paid / 100,
            currency: invoice.currency.toUpperCase(),
            nickname: _.get(invoice, 'lines.data[0].plan.nickname', null),
            finalized_at: invoice.finalized_at * 1000,
            date: invoice.date * 1000,
          }
        } else {
          return {
            ...invoice,
            id: _.get(invoice, 'charges.data[0].id', null),
            amount_due: invoice.amount / 100,
            amount_paid: invoice.amount_received / 100,
            currency: invoice.currency.toUpperCase(),
            nickname: _.get(invoice, 'charges.data[0].refunded', false) ? 'Refunded' : 'Payment',
            finalized_at: invoice.created * 1000,
            date: invoice.created * 1000,
          }
        }
      }).sort((a,b) => b.created > a.created ? 1 : -1)

      dispatch({ type: 'GET_STRIPE_INVOICES', payload: invoices })
    }
    catch(error) {
      console.log(error)
      dispatch({ type: 'GET_STRIPE_INVOICES', payload: '' })
    }
  }
}

export function createCharge(amount, selectedCreditCard = {}) {
  return async (dispatch, getState) => {
    const billingChannel = getState().plan.billingChannel
    if (billingChannel !== 'stripe') {
      return
    }
    dispatch({ type: 'SET_CUSTOMER_BALANCE_LOAD', payload: true })
    try {
      const { data: resData } = await axios.post(
        createCustomerChargeUrl,
        { amount: amount, token: (selectedCreditCard || {}).id },
        getAxiosDefaultConfig()
      )
      const { data: customerRes } = await axios.get(getCustomerInfoUrl, getAxiosDefaultConfig())
      const { customerBalance } = customerRes
      dispatch({ type: 'SET_CUSTOMER_BALANCE_PLAN', payload: parseFloat(customerBalance / 100) })
      dispatch({ type: 'SET_CUSTOMER_BALANCE_LOAD', payload: false })
    }
    catch(error) {
      console.log(error)
      let message = 'Please Select a Deposit Amount'
      dispatch({ type: 'SET_SHIPPING_TOAST_NOTIFICATION', payload: true })
      dispatch({ type: 'SET_SHIPPING_TOAST_MESSAGE', payload: message })
      dispatch({ type: 'SET_CUSTOMER_BALANCE_LOAD', payload: false })
    }
  }
}

export function fetchAiCreditPacks() {
  return async () => {
    const { data } = await axios.get(getAiCreditPacksUrl, getAxiosDefaultConfig())
    return data?.packs || []
  }
}

export function fetchAiCreditHistory(opts: {
  days?: number | 'all' | null
  limit?: number
  offset?: number
} = {}) {
  return async () => {
    const params: Record<string, string | number> = {}
    if (opts.days === 'all' || opts.days == null) {
      params.days = 'all'
    } else {
      params.days = opts.days
    }
    if (opts.limit != null) params.limit = opts.limit
    if (opts.offset != null) params.offset = opts.offset
    const { data } = await axios.get(getAiCreditHistoryUrl, {
      ...getAxiosDefaultConfig(),
      params,
    })
    return {
      history: Array.isArray(data?.history) ? data.history : [],
      total: Number(data?.total) || 0,
    }
  }
}

export function purchaseAiCredits(packId, selectedCreditCard = {}, clientNonce = '') {
  return async (dispatch, getState) => {
    const billingChannel = getState().plan.billingChannel
    if (billingChannel !== 'stripe') {
      throw new Error('AI credit purchases require a card on file')
    }
    if (!clientNonce || typeof clientNonce !== 'string') {
      throw new Error('Missing purchase idempotency nonce')
    }
    const { data } = await axios.post(
      purchaseAiCreditsUrl,
      { packId, token: (selectedCreditCard || {}).id, clientNonce },
      getAxiosDefaultConfig()
    )
    if (typeof data?.creditBalance === 'number') {
      const current = getState().plan.userCredits || {}
      dispatch({
        type: 'SET_USER_CREDITS_PLAN',
        payload: { ...current, ai: data.creditBalance },
      })
    } else {
      await dispatch(getUserCredits())
    }
    return data
  }
}

export function setBalanceTransactionsList(firstIndex, lastIndex) {
  return async (dispatch, getState) => {
    const billingChannel = getState().plan.billingChannel
    if (billingChannel !== 'stripe') {
      return
    }
    try {
      dispatch({ type: 'SET_BALANCE_FETCHING_PLAN', payload: true })
      const { data: { data: customerBalanceRes, totalCount, debits, credits } } = await axios.post(getCustomerTransactionBalanceUrl, {
        firstIndex, lastIndex
      }, getAxiosDefaultConfig())
      dispatch({ type: 'SET_CUSTOMER_BALANCE_TRANSACTION_COUNT_PLAN', payload: totalCount })
      dispatch({ type: 'SET_CUSTOMER_BALANCE_TRANSACTION_PLAN', payload: customerBalanceRes })
      dispatch({ type: 'SET_CUSTOMER_BALANCE_CREDITS', payload: parseFloat(credits / 100) })
      dispatch({ type: 'SET_CUSTOMER_BALANCE_DEBITS', payload: parseFloat(debits / 100) })
      dispatch({ type: 'SET_BALANCE_FETCHING_PLAN', payload: false })
    }
    catch(error) {
      console.log(error)
      dispatch({ type: 'SET_CUSTOMER_BALANCE_TRANSACTION_PLAN', payload: [] })
      dispatch({ type: 'SET_BALANCE_FETCHING_PLAN', payload: false })
    }
  }
}

export function updateBalanceTransactionsList() {
  return async (dispatch, getState) => {
    try {
      const { data: customerRes } = await axios.get(getCustomerInfoUrl, getAxiosDefaultConfig())
      const { customerBalance } = customerRes
      dispatch({ type: 'SET_CUSTOMER_BALANCE_PLAN', payload: parseFloat(customerBalance / 100) })
      return customerBalance / 100
    }
    catch(error) {
      console.log(error)
    }
  }
}

export function updateAutoDepositSetting(autoDepositAmount) {
  return async (dispatch, getState) => {
    try {
      await axios.post(updateAutoDepositSettingUrl, {
        autoDepositAmount
      }, getAxiosDefaultConfig())
      const { data: customerRes } = await axios.get(getCustomerInfoUrl, getAxiosDefaultConfig())
      const { autoDepositAmountSetting } = customerRes
      dispatch({ type: 'SET_AUTO_DEPOSIT_AMOUNT_SETTING_PLAN', payload: autoDepositAmountSetting })
      let message = 'Auto-Deposit settings updated'
      dispatch({ type: 'SET_SHIPPING_TOAST_NOTIFICATION', payload: true })
      dispatch({ type: 'SET_SHIPPING_TOAST_MESSAGE', payload: message })
    } catch (error) {
      console.log(error)
      let message = 'Something went wrong updating auto-deposit setting'
      dispatch({ type: 'SET_SHIPPING_TOAST_NOTIFICATION', payload: true })
      dispatch({ type: 'SET_SHIPPING_TOAST_MESSAGE', payload: message })
    }
  }
}

export function fetchAiAutoReloadSetting() {
  return async (dispatch, getState) => {
    const { data } = await axios.get(getAiAutoReloadSettingUrl, getAxiosDefaultConfig())
    const setting = data?.aiAutoReloadSetting || null
    if (setting) {
      dispatch({ type: 'SET_AI_AUTO_RELOAD_SETTING_PLAN', payload: setting })
      applyAiCreditBalanceFromSetting(dispatch, getState, setting)
    }
    return setting
  }
}

export function updateAiAutoReloadSetting(settings) {
  return async (dispatch, getState) => {
    const { data } = await axios.post(
      updateAiAutoReloadSettingUrl,
      settings,
      getAxiosDefaultConfig(),
    )
    if (data?.aiAutoReloadSetting) {
      dispatch({ type: 'SET_AI_AUTO_RELOAD_SETTING_PLAN', payload: data.aiAutoReloadSetting })
      applyAiCreditBalanceFromSetting(dispatch, getState, data.aiAutoReloadSetting)
    }
    return data?.aiAutoReloadSetting
  }
}

